import * as React from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * IconButton — an icon-only Button that cannot ship without an accessible
 * name. Pass `label` (rendered as `aria-label`) or `labelledBy`; everything
 * else (variant, loading, asChild) is plain Button API. Sizes default to the
 * square `icon` size; `icon-sm` fits dense toolbars, `icon-xs` (the 24px
 * hit-area floor) inline affordances such as tree-disclosure chevrons, and
 * `icon-touch` (44px) sits inside touch-tier inputs — the password
 * show/hide toggle on auth forms.
 *
 * Why a separate primitive: icon buttons are the classic a11y failure (empty
 * accessible name), so the type system makes the label unavoidable.
 */
export interface IconButtonProps extends Omit<
  ButtonProps,
  "size" | "children"
> {
  /** Accessible name announced to screen readers (not visually rendered). */
  label: string;
  /** Alternative to `label`: id of an element that names this button. */
  labelledBy?: string;
  size?: "icon" | "icon-sm" | "icon-xs" | "icon-touch";
  children: React.ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<IconButtonProps["size"]>, string> = {
  icon: "h-9 w-9",
  "icon-sm": "h-8 w-8",
  "icon-xs": "h-6 w-6 rounded-sm",
  "icon-touch": "h-11 w-11",
};

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      labelledBy,
      size = "icon",
      className,
      variant = "ghost",
      ...props
    },
    ref,
  ) => {
    if (!label && !labelledBy) {
      throw new Error(
        "IconButton requires a `label` (or `labelledBy`) for its accessible name.",
      );
    }
    return (
      <Button
        ref={ref}
        variant={variant}
        className={cn(SIZE_CLASSES[size], className)}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        {...props}
      />
    );
  },
);
IconButton.displayName = "IconButton";

export { IconButton };
